import React from 'react';
import { useChat } from '../../context/ChatContext';
import './LessonTopics.css';

const LessonTopics = ({ onTopicClick }) => {
  const { sendMessage, selectedChapter } = useChat();

  // Define topics based on selected chapter
  const getTopicsByChapter = () => {
    // Default astronomy topics if no chapter is selected
    if (!selectedChapter) {
      return [
        "Stellar Evolution and Life Cycles",
        "Black Holes and Relativity",
        "Planetary Systems Formation",
        "Astrophysical Jets and Energy",
        "Cosmic Microwave Background"
      ];
    }

    // Extract chapter name without section numbers or colons
    const cleanChapterName = selectedChapter.replace(/^\d+\.?\d*\s*:?\s*/i, '').trim();
    
    // Topic mapping based on chapter names
    const chapterTopics = {
      "Kinematics": [
        "Position and Displacement",
        "Velocity and Speed",
        "Acceleration",
        "Free Fall Motion",
        "Projectile Motion"
      ],
      "Forces": [
        "Newton's Laws of Motion",
        "Force and Mass",
        "Weight vs Mass",
        "Normal Force",
        "Friction"
      ],
      "Energy": [
        "Kinetic Energy",
        "Potential Energy",
        "Conservation of Energy",
        "Work and Power",
        "Simple Machines"
      ],
      "Momentum": [
        "Linear Momentum",
        "Conservation of Momentum",
        "Collisions: Elastic and Inelastic",
        "Center of Mass",
        "Impulse"
      ],
      "Gravitation": [
        "Newton's Law of Universal Gravitation",
        "Gravitational Field",
        "Orbital Motion",
        "Kepler's Laws",
        "Escape Velocity"
      ],
      "Waves": [
        "Wave Properties",
        "Standing Waves",
        "Sound Waves",
        "Doppler Effect",
        "Resonance"
      ],
      "Electromagnetism": [
        "Electric Charge and Field",
        "Magnetic Field and Forces",
        "Electromagnetic Induction",
        "Maxwell's Equations",
        "Electromagnetic Waves"
      ],
      "Optics": [
        "Reflection and Refraction",
        "Lenses and Mirrors",
        "Wave Optics",
        "Diffraction and Interference",
        "Optical Instruments"
      ],
      "Thermodynamics": [
        "Temperature and Heat",
        "Laws of Thermodynamics",
        "Heat Engines",
        "Entropy",
        "Phase Changes"
      ],
      "Quantum Physics": [
        "Wave-Particle Duality",
        "Quantum States",
        "Schrödinger Equation",
        "Uncertainty Principle",
        "Quantum Tunneling"
      ],
      "Relativity": [
        "Special Relativity",
        "Time Dilation",
        "Length Contraction",
        "Mass-Energy Equivalence",
        "General Relativity"
      ],
      "Astronomy": [
        "Stellar Evolution and Life Cycles",
        "Black Holes and Relativity",
        "Planetary Systems Formation",
        "Galaxies and Cosmic Structure",
        "Dark Matter and Dark Energy"
      ],
      "Motion": [
        "One-Dimensional Motion",
        "Two-Dimensional Motion",
        "Velocity and Acceleration",
        "Reference Frames",
        "Circular Motion"
      ],
      "Newton": [
        "First Law: Inertia",
        "Second Law: F = ma",
        "Third Law: Action-Reaction",
        "Force Diagrams",
        "Applications of Newton's Laws"
      ],
      "Work": [
        "Work Done by Constant Forces",
        "Work Done by Variable Forces",
        "Work-Energy Theorem",
        "Conservative vs Non-Conservative Forces",
        "Energy Transfer and Transformation"
      ],
      "Heat": [
        "Heat Transfer",
        "Thermal Expansion",
        "Heat Capacity",
        "Latent Heat",
        "Thermal Equilibrium"
      ],
      "Electricity": [
        "Electric Charge",
        "Electric Field",
        "Electric Potential",
        "Circuits",
        "Capacitance"
      ],
      "Magnetism": [
        "Magnetic Fields",
        "Electromagnetic Forces",
        "Electromagnetic Induction",
        "Faraday's Law",
        "Lenz's Law"
      ]
    };

    // First, try exact match with cleaned chapter name
    if (chapterTopics[cleanChapterName]) {
      return chapterTopics[cleanChapterName];
    }

    // Then try partial matches with chapter/topic keys
    for (const [topicKey, topics] of Object.entries(chapterTopics)) {
      if (cleanChapterName.toLowerCase().includes(topicKey.toLowerCase()) ||
          topicKey.toLowerCase().includes(cleanChapterName.toLowerCase())) {
        return topics;
      }
    }

    // If the chapter has multiple parts, split and try to find subtopics
    const words = cleanChapterName.split(/\s+/);
    for (const word of words) {
      if (word.length > 3) { // Only consider significant words
        for (const [topicKey, topics] of Object.entries(chapterTopics)) {
          if (topicKey.toLowerCase().includes(word.toLowerCase())) {
            return topics;
          }
        }
      }
    }

    // If no specific match is found, extract keywords from the chapter name
    // and create custom topics based on those keywords
    return [
      `Introduction to ${cleanChapterName}`,
      `Key Concepts in ${cleanChapterName}`,
      `Mathematical Formulation of ${cleanChapterName}`,
      `Applications of ${cleanChapterName}`,
      `History and Development of ${cleanChapterName}`
    ];
  };

  const topics = getTopicsByChapter();
  
  // Extract the base chapter name for the header
  const getChapterTitle = () => {
    if (!selectedChapter) return "Astronomy";
    
    // Remove section numbers
    const cleanTitle = selectedChapter.replace(/^\d+\.?\d*\s*:?\s*/i, '');
    
    // If title is too long, truncate it
    return cleanTitle.length > 30 
      ? cleanTitle.substring(0, 30) + "..." 
      : cleanTitle;
  };
  
  const chapterTitle = getChapterTitle();

  const handleTopicClick = (topic) => {
    const chapterContext = selectedChapter 
      ? `in the context of ${selectedChapter}` 
      : '';
    
    const message = `Tell me more about ${topic} ${chapterContext}`.trim();
    sendMessage(message);
    if (onTopicClick) onTopicClick();
  };

  return (
    <div className="lesson-topics">
      <div className="topics-header">⭐ {chapterTitle} Topics</div>
      <ul className="topic-list">
        {topics.map((topic, index) => (
          <li 
            key={index} 
            className="topic-item" 
            onClick={() => handleTopicClick(topic)}
          >
            <span className="topic-bullet"></span>
            {topic}
          </li>
        ))}
      </ul>
      <div className="topic-hint">✨ Click any topic to learn more! ✨</div>
    </div>
  );
};

export default LessonTopics; 